import { z } from 'zod';

// ── Extracted requirements (LLM output) ───────────────────────────────────────
// Stored as jsonb in the requirements column.

export const JobPostingRequirementsSchema = z.object({
  /** Job title as extracted from the posting text */
  role: z.string().optional(),
  /** Seniority level e.g. "Senior", "Staff", "Lead", "Junior" */
  seniority: z.string().optional(),
  /** Two-to-four sentence description of the role and team */
  summary: z.string().optional(),
  /** Hard requirements — must-have skills, experience, or qualifications */
  mustHaves: z.array(z.string()).default([]),
  /** Preferred but not required skills and qualifications */
  niceToHaves: z.array(z.string()).default([]),
  /** Key job responsibilities / day-to-day duties */
  responsibilities: z.array(z.string()).default([]),
  /** Flat list of important technical and domain keywords */
  keywords: z.array(z.string()).default([]),
});

export type JobPostingRequirements = z.infer<typeof JobPostingRequirementsSchema>;

// ── Processing status ──────────────────────────────────────────────────────────

export const JobPostingStatusSchema = z.enum([
  'pending',
  'processing',
  'completed',
  'failed',
]);

export type JobPostingStatus = z.infer<typeof JobPostingStatusSchema>;

// ── Full posting (server → client) ────────────────────────────────────────────

export const JobPostingSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  sourceUrl: z.string().url(),
  title: z.string().nullable(),
  hiringCompany: z.string().nullable(),
  location: z.string().nullable(),
  rawText: z.string().nullable(),
  requirements: JobPostingRequirementsSchema,
  keywords: z.array(z.string()),
  embeddingVersion: z.number().int(),
  status: JobPostingStatusSchema,
  errorMessage: z.string().nullable(),
  fetchedAt: z.string().datetime().nullable(),
  extractedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  /** Populated on ingest response when a similar existing posting is found */
  duplicate: z
    .object({
      id: z.string().uuid(),
      title: z.string().nullable(),
      hiringCompany: z.string().nullable(),
      createdAt: z.string().datetime(),
    })
    .nullable()
    .optional(),
});

export type JobPosting = z.infer<typeof JobPostingSchema>;

// ── Ingest request (client → server) ─────────────────────────────────────────

export const IngestJobPostingSchema = z
  .object({
    url: z.string().url('Must be a valid URL').optional(),
    rawText: z.string().min(100, 'Job description must be at least 100 characters').optional(),
  })
  .refine((d) => d.url !== undefined || d.rawText !== undefined, {
    message: 'Either a URL or job description text is required',
  });

export type IngestJobPosting = z.infer<typeof IngestJobPostingSchema>;

// ── Ingest response ────────────────────────────────────────────────────────────
// The posting record (status: pending) plus an optional duplicate warning.

export const IngestJobPostingResponseSchema = z.object({
  posting: JobPostingSchema,
  duplicate: z
    .object({
      id: z.string().uuid(),
      title: z.string().nullable(),
      hiringCompany: z.string().nullable(),
      createdAt: z.string().datetime(),
    })
    .nullable(),
});

export type IngestJobPostingResponse = z.infer<typeof IngestJobPostingResponseSchema>;
